import axios from 'axios';
import swal from "sweetalert";
import {
    loginConfirmedAction,
    Logout,
} from '../store/actions/AuthActions';
import i18next from 'i18next';
import LocalStorageType from '../constants/LocalStorageType';

export function signUp(firstName, lastName, email, password) {
    const postData = {
        firstName,
        lastName,
        email,
        password,
        returnSecureToken: true,
    };
    return axios.post(
        process.env.REACT_APP_API_URL + "/signup",
        postData,
    );
}

export function login(email, password) {
    const postData = {
        username: email,
        password,
        type: "WEB",
    };
    return axios.post(
        process.env.REACT_APP_API_URL + "/auth",
        postData,
    );
}

export function formatError(errorResponse) {
    if (!errorResponse) return i18next.t("error");

    switch (errorResponse.message) {
        case 'EMAIL_EXISTS':
            return "Email already exists";
        case 'EMAIL_NOT_FOUND':
            return "Email not found";
        case 'INVALID_PASSWORD':
            return i18next.t("validation.invalid.password");
        case 'USER_BLOCKED':
            return i18next.t("validation.custom.login_user_blocked");
        case 'USER_NOT_ACTIVATED':
            return i18next.t("validation.custom.login_user_not_activated");
        case 'USER_NOT_ACTIVATED_ACCOUNT':
            return i18next.t("validation.custom.login_user_not_activated_account");
        default:
            return errorResponse.message || i18next.t("error");
    }
}

// Grava a resposta da autenticação no LocalStorage.
export function saveTokenInLocalStorage(tokenDetails) {
    // Gera a data de expiração a partir dos segundos 
    // de expiraçào fornecido na resposta do login.
    tokenDetails.expireDate = new Date(
        new Date().getTime() + tokenDetails.expiresIn * 1000,
    );

    localStorage.setItem(LocalStorageType.USER_DETAILS, JSON.stringify(tokenDetails));
}

// Timer único de logout automático.
// WHY: cada chamada de runLogoutTimer criava um setTimeout novo sem cancelar o anterior.
// Com a renovação transparente do token isso viraria um bug visível: o timer velho dispararia
// o logout no vencimento antigo mesmo com a sessão já renovada, derrubando o operador.
let logoutTimerId = null;

// Guarda o navigate do último agendamento para as renovações reaproveitarem o mesmo
// redirecionamento, já que elas acontecem no interceptor, longe do componente com o router.
let logoutNavigate = null;

export function runLogoutTimer(dispatch, timer, navigate) {
    if (logoutTimerId) {
        clearTimeout(logoutTimerId);
    }

    if (navigate) {
        logoutNavigate = navigate;
    }

    logoutTimerId = setTimeout(() => {
        logoutTimerId = null;
        dispatch(Logout(logoutNavigate));
    }, timer);
}

/**
 * Aplica o token renovado que o backend devolve nos headers quando o atual está perto de vencer.
 * Mantém no Local Storage os demais dados da sessão (permissões, usuário) e apenas troca o token
 * e a validade, para o operador não ser deslogado no meio do expediente.
 *
 * @returns {Object|null} Sessão atualizada, ou null quando não há nada para renovar.
 */
export function applyRenewedToken(newToken, expiresInSeconds) {
    if (!newToken) {
        return null;
    }

    try {
        const stored = localStorage.getItem(LocalStorageType.USER_DETAILS);
        if (!stored) {
            return null;
        }

        const tokenDetails = JSON.parse(stored);

        // Sem os segundos informados pelo backend não dá para reprogramar o logout com
        // segurança; nesse caso mantém a validade anterior em vez de inventar um prazo.
        const expiresIn = Number(expiresInSeconds) > 0
            ? Number(expiresInSeconds)
            : tokenDetails.expiresIn;

        tokenDetails.token = newToken;
        tokenDetails.expiresIn = expiresIn;
        tokenDetails.expireDate = new Date(new Date().getTime() + expiresIn * 1000);

        localStorage.setItem(LocalStorageType.USER_DETAILS, JSON.stringify(tokenDetails));
        return tokenDetails;
    } catch (error) {
        // Local Storage corrompido ou indisponível não pode quebrar a requisição em curso:
        // a sessão segue valendo com o token anterior até o vencimento dele.
        return null;
    }
}

// Verifica se o token é válido.
export function checkAutoLogin(dispatch, navigate) {
    const tokenDetailsString = localStorage.getItem(LocalStorageType.USER_DETAILS);

    let tokenDetails = '';
    if (!tokenDetailsString) {
        dispatch(Logout(navigate));
        return;
    }
    
    tokenDetails = JSON.parse(tokenDetailsString);
    let expireDate = new Date(tokenDetails.expireDate);
    let todaysDate = new Date();

    if (todaysDate > expireDate) {
        dispatch(Logout(navigate));
        return;
    }

    dispatch(loginConfirmedAction(tokenDetails));

    const timer = expireDate.getTime() - todaysDate.getTime();
    runLogoutTimer(dispatch, timer, navigate);
}